import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Dropdown, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';
import SearchBoxDropdown from 'react-lib/appcon/common/SearchBoxDropdown';

const PRODUCT_SEARCH_ID = "MCProduct";

type CardMCSummaryProductProps = {
  controller: any;
  runSequence: any;
  MeChartSequence: any;
  productTypeOptions: any;
  packageTypeOptions: any;
  searchedItemListWithKey?: Record<string, any>;
}

const thaiMonths = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const CardMCSummaryProduct = (props: CardMCSummaryProductProps) => {
  console.log('CardMCSummaryProduct props: ', props);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);

  const [category, setCategory] = useState<'popular' | 'revenue_max' | 'sales_sum' | 'revenue_sum'>('popular');
  const [subView, setSubView] = useState<'top5' | 'top3' | 'product'>('top5');
  const [productType, setProductType] = useState<number | string | null>("ALL");
  const [productSearch, setProductSearch] = useState<number | null>(null);

  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    props.controller.handleEvent({
      message: "GetMasterData",
      params: {
        masters: [
          ["productType", {}],
          ["packageType", {}]
        ],
      },
    });
  }, [])
  

  const fetchData = async () => {
    setLoading(true);
    try {
      
      const lastDay = new Date(year, endMonth, 0).getDate();
      const startDateStr = subView === 'top3'
        ? `${year - 2}-01-01 00:00:00`
        : `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
      const endDateStr = subView === 'top3'
        ? `${year}-12-31 23:59:59`
        : `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;

      const chartKey = subView === 'top5'
        ? 'summary-product-top5'
        : subView === 'top3'
          ? 'summary-product-top3-year'
          : 'summary-product-monthly';

      if (props.MeChartSequence?.sequenceIndex === "Action") {
        const res = await props.runSequence({
          sequence: "MeChart",
          action: "QuerySQL",
          chart: chartKey,
          category: category,
          startDate: startDateStr,
          endDate: endDateStr,
          ...(subView === 'product' && productSearch ? { productId: productSearch } : {}),
          ...(subView !== 'product' && productType && productType !== "ALL" ? { productType } : {}),
        });

        if (Array.isArray(res) && res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          setChartData(res[0].items);
        } else {
          setChartData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (subView === 'product') return;
    fetchData();
  }, [category, subView, year]);

  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(chartData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SummaryProduct");
    XLSX.writeFile(wb, `Summary_Product_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Data for Vega — derived from chartData

  // 1. Top 5 (Vertical Bar)
  const getVegaTop5Data = () => {
    const valueField = category === 'popular' ? 'total_sold' : 'total_price';
    return chartData.map((d: any, i: number) => ({
      pricing_name: d.pricing_name,
      value: parseInt(d[valueField] || 0),
      color: '#F06292',
      order: i,
    }));
  };

  // 2. Top 3 Years (Horizontal Grouped Bar)
  const getVegaTop3YearData = () => {
    const rankColors: Record<number, string> = { 1: '#1976D2', 2: '#FF0000', 3: '#32CD32' };
    const valueField = category === 'popular' ? 'total_sold' : 'total_price';
    return chartData.map((d: any) => ({
      year: String(d.year),
      product_name: d.product_name,
      rank_position: String(d.rank_position),
      value: parseInt(d[valueField] || 0),
      color: rankColors[d.rank_position] || '#1976D2',
    }));
  };

  // 3. Monthly Product (Vertical Bar)
  const getVegaMonthlyData = () => {
    const valueField = category === 'sales_sum' ? 'total_sold' : 'total_price';
    return chartData.map((d: any) => ({
      month: thaiMonths[parseInt(d.month) - 1],
      value: parseInt(d[valueField] || 0),
      color: '#E91E63',
      order: parseInt(d.month) - 1,
    }));
  };

  const getSpec = (): VisualizationSpec => {
    // 1. Top 5 (Vertical Bar)
    if (subView === 'top5') {
      const data = getVegaTop5Data();
      const yTitle = category === 'popular' ? 'Quantity' : 'Revenue (THB)';

      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "bar", tooltip: true },
        encoding: {
          x: {
            field: "pricing_name",
            type: "ordinal",
            axis: { title: "Product", labelAngle: -45, labelLimit: 200 },
            sort: { field: "order" }
          },
          y: { field: "value", type: "quantitative", axis: { title: yTitle } },
          color: { field: "color", type: "nominal", scale: null },
          tooltip: [
            { field: "pricing_name", title: "Product" },
            { field: "value", title: yTitle, format: "," }
          ]
        }
      };
    }

    // 2. Top 3 Years (Horizontal Grouped Bar)
    if (subView === 'top3') {
      const data = getVegaTop3YearData();
      const xTitle = category === 'popular' ? 'Quantity' : 'Revenue (THB)';

      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "bar", tooltip: true },
        encoding: {
          y: { field: "year", type: "ordinal", axis: { title: "Year" } },
          x: { field: "value", type: "quantitative", axis: { title: xTitle } },
          yOffset: { field: "rank_position", type: "ordinal" },
          color: {
            field: "product_name",
            type: "nominal",
            scale: { range: ['#1976D2', '#FF0000', '#32CD32'] },
            legend: { title: "Product" }
          },
          tooltip: [
            { field: "year", title: "Year" },
            { field: "product_name", title: "Product" },
            { field: "rank_position", title: "Rank" },
            { field: "value", title: xTitle, format: "," }
          ]
        }
      };
    }

    // 3. Monthly Product (Vertical Bar)
    if (subView === 'product') {
      const data = getVegaMonthlyData();
      const yTitle = category === 'sales_sum' ? 'Sales Quantity' : 'Revenue (THB)';

      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        mark: { type: "bar", tooltip: true },
        encoding: {
          x: {
            field: "month",
            type: "ordinal",
            axis: { title: "Month", labelAngle: -45 },
            sort: { field: "order" }
          },
          y: { field: "value", type: "quantitative", axis: { title: yTitle } },
          color: { field: "color", type: "nominal", scale: null },
          tooltip: [
            { field: "month", title: "Month" },
            { field: "value", title: yTitle, format: "," }
          ]
        }
      };
    }

    return { mark: "bar", data: { values: [] } };
  };

  const handleCategoryChange = (newCat: 'popular' | 'revenue_max' | 'sales_sum' | 'revenue_sum') => {
    setCategory(newCat);
    if (newCat === 'sales_sum' || newCat === 'revenue_sum') {
      setSubView('product');
    } else {
      if (subView === 'product') {
        setSubView('top5');
      }
    }
  };

  return (
    <div id="card-mc-summary-product">
      {/* Container for Controls */}
      <div className="controls-wrapper">

        {/* Row 1: Main Category + Bar Toggle */}
        <div className="controls-container">
          <Button.Group vertical={false} style={{ flexWrap: 'wrap' }}>
            <Button
              active={category === 'popular'}
              style={category === 'popular' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => handleCategoryChange('popular')}
            >
              สินค้า และ บริการยอดนิยม
            </Button>
            <Button
              active={category === 'revenue_max'}
              style={category === 'revenue_max' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => handleCategoryChange('revenue_max')}
            >
              สินค้า และ บริการรายได้สูงสุด
            </Button>
            <Button
              active={category === 'sales_sum'}
              style={category === 'sales_sum' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => handleCategoryChange('sales_sum')}
            >
              สรุปยอดขาย
            </Button>
            <Button
              active={category === 'revenue_sum'}
              style={category === 'revenue_sum' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => handleCategoryChange('revenue_sum')}
            >
              สรุปรายได้
            </Button>
          </Button.Group>

          {/* Separate Bar Chart Toggle (Far Right) */}
          <div>
            <Button.Group>
              <Button
                active={true}
                style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }}
                icon
              >
                <Icon name='chart bar' /> Bar
              </Button>
            </Button.Group>
          </div>
        </div>

        {/* Row 2: Sub View */}
        <div>
          <Button.Group vertical={false}>
            <Button
              active={subView === 'top5'}
              style={subView === 'top5' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setSubView('top5')}
              disabled={category === 'sales_sum' || category === 'revenue_sum'}
            >
              5 อันดับ แต่ละเดือน
            </Button>
            <Button
              active={subView === 'top3'}
              style={subView === 'top3' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setSubView('top3')}
              disabled={category === 'sales_sum' || category === 'revenue_sum'}
            >
              3 อันดับใน 3 ปีย้อนหลัง
            </Button>
            <Button
              active={subView === 'product'}
              style={subView === 'product' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
              onClick={() => setSubView('product')}
              disabled={category === 'popular' || category === 'revenue_max'}
            >
              แต่ละ product
            </Button>
          </Button.Group>
        </div>

      </div>

      {/* Row 3: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={subView === 'top3' ? 'yearOnly' : 'oneYearMonth'}
          year={year}
          month={month}
          endYear={year}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={() => { }}
          onEndMonthChange={setEndMonth}
          onSearch={fetchData}
          onPrint={onPrint}
          searchDisabled={subView === 'product' && !productSearch}
          customFilter={
            (subView === 'top5' || subView === 'top3') ? (
              <Dropdown
                selection
                compact
                placeholder='ประเภทสินค้า'
                options={[{ key: "ALL", text: "ALL", value: "ALL" }, ...(props.productTypeOptions || [])]}
                value={productType ?? "ALL"}
                onChange={(_, { value }) => setProductType(value as number | string)}
                style={{ minWidth: '200px' }}
              />
            ) : subView === 'product' ? (
              <SearchBoxDropdown
                onEvent={props.controller.handleEvent}
                type="ProductAll"
                id={PRODUCT_SEARCH_ID}
                icon="search"
                limit={20}
                placeholder='ค้นหาสินค้า'
                selectedItem={productSearch}
                searchedItemListWithKey={props.searchedItemListWithKey}
                useWithKey
                style={{ minWidth: '250px' }}
                setSelectedItem={(value) => setProductSearch(value as number || null)}
              />
            ) : undefined
          }
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCSummaryProduct;
